import type { AnalysisResult } from './api';
import { businessLabel } from '../lib/user-facing';

export const visualDate = (value?: string) => {
  if (!value || value === 'unavailable') return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not available'
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export function VisualSources({
  result,
  expanded = false,
}: {
  result: AnalysisResult;
  expanded?: boolean;
}) {
  const sources = result.context?.sources ?? [];
  const fields = sources[0]?.fields ?? result.context?.recordColumns ?? [];
  return (
    <details
      open={expanded}
      className="group overflow-hidden rounded-xl border border-border-strong bg-surface shadow-sm"
    >
      <summary className="cursor-pointer bg-muted/60 px-5 py-4 focus-visible:ring-2 focus-visible:ring-ring">
        <span className="ml-1 text-sm font-semibold">Sources, definitions and quality</span>
        <span className="ml-3 text-xs text-muted-foreground">
          {sources.length
            ? `${sources.length} ${sources.length === 1 ? 'dataset' : 'datasets'} · published snapshots`
            : 'Governed module data'}
        </span>
      </summary>
      <div className="space-y-5 border-t border-border-strong px-5 py-4 text-sm">
        {result.context?.timezone && <p className="text-xs text-muted-foreground">Reporting dates use {result.context.timezone}.</p>}
        {result.analysis.baselineComparison && <p className="rounded-lg border border-border bg-muted p-3 text-xs leading-5">
          This comparison independently aggregates {businessLabel(result.analysis.view)} and the published baseline dataset below.
          Chart values show matched months and reductions. Inspect underlying records in each source separately.
        </p>}
        {sources.map((source) => (
          <section
            key={source.datasetId}
            className="space-y-3 border-b border-border pb-5 last:border-b-0 last:pb-0"
          >
            <div>
              <h3 className="font-semibold">
                {source.name}{' '}
                <span className="font-normal text-muted-foreground">
                  · version {source.version}
                  {source.checkpoint ? ` · checkpoint ${source.checkpoint}` : ''}
                </span>
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {source.filename ?? (source.origin ? 'PostgreSQL' : 'Dataset')} / {source.source} ·{' '}
                {source.rows.toLocaleString()} rows
              </p>
            </div>
            <dl className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <dt className="text-muted-foreground">Published</dt>
                <dd className="mt-1 font-medium">{visualDate(source.publishedAt)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Source date</dt>
                <dd className="mt-1 font-medium">
                  {source.sourceDate ? visualDate(source.sourceDate) : 'Not supplied'}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-muted-foreground">Row definition</dt>
                <dd className="mt-1">{source.grain || 'One source table record'}</dd>
              </div>
            </dl>
            <p className="rounded-lg border border-border bg-muted px-3 py-2 text-xs">
              {source.quality.rejected.toLocaleString()} rejected ·{' '}
              {source.quality.skipped.toLocaleString()} duplicates skipped
            </p>
            {!!source.inputs?.length && (
              <details className="text-xs">
                <summary className="cursor-pointer font-medium">
                  Contributing files ({source.inputs.length})
                </summary>
                <ul className="mt-2 space-y-2">
                  {source.inputs.map((input) => (
                    <li key={input.importId}>
                      {input.filename} / {input.source} · {input.accepted.toLocaleString()} accepted
                      · source date{' '}
                      {input.sourceDate ? visualDate(input.sourceDate) : 'not supplied'}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <caption className="sr-only">{source.name} field definitions</caption>
                <thead className="bg-muted">
                  <tr className="border-b border-border">
                    <th className="py-2 pr-3">Field</th>
                    <th className="pr-3">Type</th>
                    <th className="pr-3">Unit</th>
                    <th>Aggregation</th>
                  </tr>
                </thead>
                <tbody>
                  {source.fields.map((field) => (
                    <tr key={field.id} className="border-b border-border/50">
                      <th className="py-2 pr-3 font-medium">{field.name}</th>
                      <td className="pr-3">{businessLabel(field.type)}</td>
                      <td className="pr-3">{field.currency ?? field.unit ?? '—'}</td>
                      <td>{field.aggregations.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
        {!sources.length && (
          <p>
            {businessLabel(result.analysis.view)}.{' '}
            {result.context?.recordColumns?.length
              ? 'Underlying records use the current governed reporting data.'
              : 'Underlying records are unavailable for this visual.'}
          </p>
        )}
        {!!result.context?.relationships?.length && (
          <section>
            <h3 className="mb-2 font-medium">Approved relationships</h3>
            {result.context.relationships.map((relation) => (
              <p key={relation.id} className="mb-2 text-xs text-muted-foreground">
                {fields.find((field) => field.id === relation.sourceField)?.name} →{' '}
                {sources.find((source) => source.datasetId === relation.targetDatasetId)?.name ??
                  businessLabel(relation.entity ?? '')}{' '}
                · {relation.cardinality.replace(/_/g, ' ')} · {relation.unmatched.toLocaleString()}{' '}
                unmatched when approved
              </p>
            ))}
          </section>
        )}
        {result.context?.internalFreshness && (
          <p className="text-xs text-muted-foreground">
            Internal modules last observed: {visualDate(result.context.internalFreshness)}. This is
            separate from file publication dates.
          </p>
        )}
        <p className="text-xs leading-5 text-muted-foreground">
          Files are published snapshots. Underlying records follow the current filters; a top-N
          chart may display only a subset of them.
        </p>
      </div>
    </details>
  );
}
